import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api, useAuth } from "../context/AuthContext";
import { money, dateFmt } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "./ui/dialog";
import { PiggyBank, Plus, PencilSimple, Trash, FilePdf, ArrowDown, ArrowUp, MagnifyingGlass, FileXls, IdentificationCard, UploadSimple, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LinkClientButton } from "./LinkClientButton";

const empty = { first_name: "", last_name: "", national_id: "", address: "", phone: "", email: "", balance: "", currency: "DOP", rate: "", lock_months: "", early_penalty_pct: "" };

const PhotoDialog = ({ account, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(account.photo_url || null);
  const [dirty, setDirty] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => { if (open) { setPhotoUrl(account.photo_url || null); setDirty(false); } }, [open]); // eslint-disable-line

  const closeAndRefresh = (o) => { setOpen(o); if (!o && dirty) onDone(); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/savings-accounts/${account.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(data.photo_url); setDirty(true);
      toast.success(t("photo_saved"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = async () => {
    setBusy(true);
    try { await api.delete(`/admin/savings-accounts/${account.id}/photo`); setPhotoUrl(null); setDirty(true); toast.success("OK"); }
    catch (err) { toast.error("Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={closeAndRefresh}>
      <DialogTrigger asChild>
        <Button data-testid={`photo-savings-${account.id}`} title={t("client_photo")} aria-label={t("client_photo")} size="sm" variant="outline" className={account.photo_url ? "border-emerald-400 text-emerald-700 hover:bg-emerald-50" : "border-slate-300 text-slate-500 hover:bg-slate-50"}><IdentificationCard size={15} weight="bold" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="photo-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("client_photo")} — {account.account_number}</DialogTitle>
          <DialogDescription>{account.full_name}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          {photoUrl ? (
            <img data-testid="photo-preview" src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`} alt={account.full_name} className="w-48 h-48 object-cover rounded-xl border border-gray-200" />
          ) : (
            <div className="w-48 h-48 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300"><IdentificationCard size={64} weight="duotone" /></div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="photo-file-input" />
          <div className="flex gap-2">
            <Button data-testid="photo-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 gap-1.5"><UploadSimple size={16} weight="bold" /> {photoUrl ? t("replace_photo") : t("upload_photo")}</Button>
            {photoUrl && <Button data-testid="photo-delete-btn" onClick={removePhoto} disabled={busy} title={t("delete")} aria-label={t("delete")} variant="outline" className="border-red-200 text-red-500 hover:bg-red-50"><Trash size={16} weight="bold" /></Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MovementDialog = ({ account, kind, onDone }) => {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const isDeposit = kind === "deposit";

  useEffect(() => { if (open) setAmount(""); }, [open]);

  const submit = async () => {
    const val = Number(amount);
    if (!val || val <= 0) { toast.error(t("invalid_amount")); return; }
    setBusy(true);
    try {
      await api.post(`/admin/savings-accounts/${account.id}/${isDeposit ? "deposit" : "withdraw"}`, { amount: val });
      toast.success("OK"); setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`${isDeposit ? "deposit" : "withdraw"}-savings-${account.id}`}
          title={isDeposit ? t("deposit") : t("withdraw")}
          aria-label={isDeposit ? t("deposit") : t("withdraw")}
          size="sm" variant="outline"
          className={isDeposit ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1" : "border-amber-300 text-amber-700 hover:bg-amber-50 gap-1"}
        >
          {isDeposit ? <ArrowDown size={15} weight="bold" /> : <ArrowUp size={15} weight="bold" />}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="movement-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{isDeposit ? t("deposit") : t("withdraw")} — {account.account_number}</DialogTitle>
          <DialogDescription>{t("holder")}: {account.full_name} · {t("current_balance")}: {money(account.balance, account.currency)}</DialogDescription>
        </DialogHeader>
        {!isDeposit && account.locked && (
          <div data-testid="movement-lock-warn" className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
            <LockKey size={16} weight="fill" className="mt-0.5 shrink-0" />
            <span>{t("locked_until")} {dateFmt(account.unlock_date, lang)}. {t("lock_penalty_note")} {account.early_penalty_pct}%.</span>
          </div>
        )}
        <div>
          <Label>{t("mv_amount")} ({account.currency})</Label>
          <Input data-testid="movement-amount" type="number" step="0.01" min="0" className="mt-1 h-11 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </div>
        <DialogFooter>
          <Button data-testid="movement-submit" onClick={submit} disabled={busy} className={isDeposit ? "bg-emerald-800 hover:bg-emerald-900" : "bg-amber-600 hover:bg-amber-700"}>{busy ? t("loading") : (isDeposit ? t("deposit") : t("withdraw"))}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SavingsDialog = ({ editing, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setF(editing ? { ...empty, ...editing, balance: String(editing.balance), rate: editing.rate_custom ? String(editing.rate) : "", lock_months: editing.lock_months ? String(editing.lock_months) : "", early_penalty_pct: editing.early_penalty_pct ? String(editing.early_penalty_pct) : "" } : empty);
  }, [open]); // eslint-disable-line

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f, balance: Number(f.balance) || 0, rate: f.rate === "" ? null : Number(f.rate), lock_months: Number(f.lock_months) || 0, early_penalty_pct: Number(f.early_penalty_pct) || 0 };
      if (editing) await api.put(`/admin/savings-accounts/${editing.id}`, payload);
      else await api.post("/admin/savings-accounts", payload);
      toast.success("OK"); setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <Button data-testid={`edit-savings-${editing.id}`} title={t("edit")} aria-label={t("edit")} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><PencilSimple size={15} weight="bold" /></Button>
          : <Button data-testid="new-savings-btn" className="bg-emerald-800 hover:bg-emerald-900 gap-1.5"><Plus size={16} weight="bold" /> {t("new_savings")}</Button>}
      </DialogTrigger>
      <DialogContent data-testid="savings-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{editing ? t("edit") : t("new_savings")}</DialogTitle>
          <DialogDescription className="sr-only">{t("new_savings")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t("first_name")}</Label><Input data-testid="sv-first" className="mt-1 h-10" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} /></div>
          <div><Label>{t("last_name")}</Label><Input data-testid="sv-last" className="mt-1 h-10" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} /></div>
          <div className="col-span-2"><Label>{t("national_id")}</Label><Input data-testid="sv-national-id" className="mt-1 h-10" placeholder={t("national_id_ph")} value={f.national_id} onChange={(e) => setF({ ...f, national_id: e.target.value })} /></div>
          <div><Label>{t("email")}</Label><Input data-testid="sv-email" className="mt-1 h-10" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>{t("phone")}</Label><Input data-testid="sv-phone" className="mt-1 h-10" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div className="col-span-2"><Label>{t("address")}</Label><Input data-testid="sv-address" className="mt-1 h-10" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          {!editing && <div><Label>{t("deposit_amount")}</Label><Input data-testid="sv-balance" type="number" step="0.01" className="mt-1 h-10 font-mono-num" value={f.balance} onChange={(e) => setF({ ...f, balance: e.target.value })} /></div>}
          <div>
            <Label>{t("currency")}</Label>
            <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
              <SelectTrigger data-testid="sv-currency" className="mt-1 h-10"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="DOP">RD$ (DOP)</SelectItem><SelectItem value="USD">US$ (USD)</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("sv_rate")}</Label>
            <Input data-testid="sv-rate" type="number" step="0.01" min="0" max="100" className="mt-1 h-10 font-mono-num" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} placeholder="3.00" />
            <p className="text-[11px] text-slate-400 mt-1">{t("sv_rate_hint")}</p>
          </div>
          <div>
            <Label>{t("sv_lock_months")}</Label>
            <Input data-testid="sv-lock-months" type="number" step="1" min="0" className="mt-1 h-10 font-mono-num" value={f.lock_months} onChange={(e) => setF({ ...f, lock_months: e.target.value })} placeholder="0" />
          </div>
          <div>
            <Label>{t("sv_penalty")}</Label>
            <Input data-testid="sv-penalty" type="number" step="0.01" min="0" max="100" className="mt-1 h-10 font-mono-num" value={f.early_penalty_pct} onChange={(e) => setF({ ...f, early_penalty_pct: e.target.value })} placeholder="0" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="sv-submit" onClick={submit} disabled={busy || !f.first_name.trim() || !f.last_name.trim()} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("loading") : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SavingsAccountsPanel = ({ initialMonth } = {}) => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canTx = (user?.permissions || []).includes("manage_transactions");
  const [items, setItems] = useState([]);
  const [locks, setLocks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [verifs, setVerifs] = useState([]);
  useEffect(() => { if (initialMonth) setMonthFilter(initialMonth); }, [initialMonth]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = () => {
    setLoading(true); setError("");
    api.get("/admin/savings-locks?days=400").then((r) => setLocks(r.data)).catch(() => {});
    api.get("/admin/branches").then((r) => setBranches(r.data.branches || [])).catch(() => {});
    api.get("/admin/verification-logs").then((r) => setVerifs(r.data)).catch(() => {});
    return api.get("/admin/savings-accounts")
      .then((r) => setItems(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete_savings"))) return;
    try { await api.delete(`/admin/savings-accounts/${id}`); toast.success("OK"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadCert = async (s) => {
    try {
      const res = await api.get(`/admin/savings-accounts/${s.id}/certificate`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `certificado_ahorro_${s.account_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/admin/savings-accounts/export${search ? `?q=${encodeURIComponent(search)}` : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "cuentas_ahorro.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const q = search.toLowerCase();
  const filtered = items.filter((s) =>
    (s.account_number || "").toLowerCase().includes(q) ||
    (s.full_name || "").toLowerCase().includes(q) ||
    (s.email || "").toLowerCase().includes(q) ||
    (s.phone || "").toLowerCase().includes(q) ||
    (s.folio || "").toLowerCase().includes(q)
  );

  const lockMonths = [...new Set(locks.map((s) => (s.unlock_date || "").slice(0, 7)).filter(Boolean))].sort();
  const filteredLocks = locks.filter((s) =>
    (branchFilter === "all" || (s.branch || "Principal") === branchFilter) &&
    (monthFilter === "all" || (s.unlock_date || "").slice(0, 7) === monthFilter)
  );

  return (
    <div className="space-y-5" data-testid="savings-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <PiggyBank size={22} weight="duotone" className="text-emerald-700" />
          <h3 className="font-display font-bold text-emerald-900 text-xl">{t("savings_accounts")}</h3>
        </div>
        <SavingsDialog onDone={load} />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative max-w-sm w-full">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="savings-search" placeholder={t("search_client")} className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button data-testid="savings-export-btn" onClick={exportExcel} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><FileXls size={16} weight="bold" /> Excel</Button>
      </div>
      {locks.length > 0 && (
        <div data-testid="savings-locks-section" className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <LockKey size={18} weight="fill" className="text-amber-600" />
              <h4 className="font-display font-bold text-amber-900">{t("locks_title")}</h4>
            </div>
            <div className="flex items-center gap-2">
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger data-testid="locks-branch-filter" className="h-9 w-40 bg-white"><SelectValue placeholder={t("filter_branch")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_branches")}</SelectItem>
                  {branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger data-testid="locks-month-filter" className="h-9 w-36 bg-white"><SelectValue placeholder={t("filter_month")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_months")}</SelectItem>
                  {lockMonths.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            {filteredLocks.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-3">—</p>
            ) : filteredLocks.map((s) => (
              <div key={s.id} data-testid={`lock-item-${s.id}`} className="flex items-center justify-between flex-wrap gap-1 text-sm bg-white rounded-lg px-3 py-2 border border-amber-100">
                <div><span className="font-mono-num font-semibold text-emerald-800">{s.account_number}</span> <span className="text-slate-500">· {s.full_name}</span> <span className="text-[11px] text-slate-400">· {s.branch || "Principal"}</span></div>
                <div className="text-right"><span className="text-amber-700 font-semibold">{t("locked_until")} {dateFmt(s.unlock_date, lang)}</span><span className="text-[11px] text-slate-400 ml-2">({s.days_left} {t("days_left")}{s.early_penalty_pct ? ` · ${s.early_penalty_pct}%` : ""})</span></div>
              </div>
            ))}
          </div>
        </div>
      )}
      {verifs.length > 0 && (
        <div data-testid="verif-section" className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h4 className="font-display font-bold text-emerald-900 mb-2">{t("verif_title")}</h4>
          <div className="space-y-1">
            {verifs.slice(0, 8).map((v) => (
              <div key={v.code} data-testid={`verif-${v.code}`} className="flex items-center justify-between text-sm">
                <span className="font-mono-num text-emerald-800">{v.code}</span>
                <span className="text-slate-500">{v.count} {t("verif_times")} · {dateFmt(v.last_at, lang)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("account_number")}</TableHead>
              <TableHead>{t("holder")}</TableHead>
              <TableHead>{t("current_balance")}</TableHead>
              <TableHead>{t("accrued_earnings")}</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">{t("loading")}</TableCell></TableRow>
            ) : error ? (
              <TableRow><TableCell colSpan={5} className="text-center text-red-500 py-8" data-testid="savings-error">{error}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">—</TableCell></TableRow>
            ) : filtered.map((s) => (
              <TableRow key={s.id} data-testid={`savings-row-${s.id}`}>
                <TableCell className="font-mono-num text-emerald-800 font-semibold">{s.account_number}<div className="text-[11px] text-slate-400 font-normal">{t("folio")}: {s.folio}</div></TableCell>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    {s.photo_url ? (
                      <img data-testid={`photo-thumb-${s.id}`} src={`${process.env.REACT_APP_BACKEND_URL}${s.photo_url}`} alt={s.full_name} className="w-9 h-9 rounded-full object-cover border border-emerald-200 flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-300 flex-shrink-0"><IdentificationCard size={18} weight="duotone" /></div>
                    )}
                    <div>
                      <div className="font-semibold text-emerald-900">{s.full_name}</div>
                      <div className="text-xs text-slate-400">{s.email || s.phone}</div>
                      {s.national_id && <div className="text-[11px] text-slate-500">{t("national_id")}: {s.national_id}</div>}
                      {s.user_id && <span className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{t("linked")}</span>}
                      {s.locked && <span data-testid={`lock-badge-${s.id}`} className="inline-block mt-1 ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{t("lock_badge")} · {s.unlock_date}</span>}
                      {s.user_id && s.linked_by && <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`linked-info-${s.id}`}>{t("linked_by")}: {s.linked_by}{s.linked_at ? ` · ${dateFmt(s.linked_at, lang)}` : ""}</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono-num font-bold text-emerald-900">{money(s.balance, s.currency)}</TableCell>
                <TableCell className="font-mono-num" data-testid={`savings-earnings-${s.id}`}>
                  <span className="font-bold text-emerald-700">{money(s.earnings, s.currency)}</span>
                  <div className="text-[11px] text-slate-400 font-normal">{Number(s.rate || 0).toFixed(2)}% {t("per_year")}</div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    {canTx && <MovementDialog account={s} kind="deposit" onDone={load} />}
                    {canTx && <MovementDialog account={s} kind="withdraw" onDone={load} />}
                    <PhotoDialog account={s} onDone={load} />
                    <LinkClientButton base="/admin/savings-accounts" account={s} onDone={load} />
                    <Button data-testid={`cert-savings-${s.id}`} title={t("certificate")} aria-label={t("certificate")} size="sm" variant="outline" onClick={() => downloadCert(s)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FilePdf size={15} weight="bold" /></Button>
                    <SavingsDialog editing={s} onDone={load} />
                    <Button data-testid={`del-savings-${s.id}`} title={t("delete")} aria-label={t("delete")} size="sm" variant="outline" onClick={() => del(s.id)} className="border-red-200 text-red-500 hover:bg-red-50"><Trash size={15} weight="bold" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
